import { Controller } from "@hotwired/stimulus"
import {
  performanceSignaturesFromDom,
  scheduleSignatures,
  signaturesDiffer
} from "../../pwa/compare_schedule_entries"
import { cloneRowFromTemplate, prefillRowFromScheduleEntry } from "../../pwa/performance_row_builder"

// Connects to data-controller="attandances--schedule-diff"
//
// Shows a warning banner when the saved performances drift from the current
// schedule entries for this attendance's worker/date, and offers a button that
// rebuilds the performance rows from the schedule.
//
// Only runs when the rendered shell is for today. A stale (offline) shell is
// owned by attandances--schedule-hydrator, which rebuilds the form itself.
export default class extends Controller {
  static targets = ["banner", "container", "template"]
  static values = { workerId: String, date: String }

  connect() {
    this.#evaluate()
  }

  applySchedule(event) {
    event.preventDefault()

    const entries = this.#scheduleEntries()
    const container = this.containerTarget
    const template = this.templateTarget

    container.querySelectorAll(".work-item-container").forEach(row => {
      const idInput = row.querySelector('input[name$="[id]"]')
      const destroyInput = row.querySelector('input[name$="[_destroy]"]')
      if (idInput && idInput.value) {
        row.style.display = "none"
        if (destroyInput) destroyInput.value = "true"
      } else {
        row.remove()
      }
    })

    entries.forEach(entry => {
      const row = cloneRowFromTemplate(template)
      if (!row) return
      prefillRowFromScheduleEntry(row, entry)
      container.appendChild(row)
    })

    this.#hideBanner()
  }

  #evaluate() {
    if (!this.hasBannerTarget || !this.hasContainerTarget) return
    if (this.#shellIsStale()) {
      this.#hideBanner()
      return
    }

    const entries = this.#scheduleEntries()
    if (entries.length === 0) {
      this.#hideBanner()
      return
    }

    const perfSignatures = performanceSignaturesFromDom(this.containerTarget)
    const scheduleSigs = scheduleSignatures(entries)

    if (signaturesDiffer(perfSignatures, scheduleSigs)) {
      this.#showBanner()
    } else {
      this.#hideBanner()
    }
  }

  #scheduleEntries() {
    const script = document.getElementById("weekly-schedule-entries-data")
    if (!script) return []

    let data = {}
    try {
      data = JSON.parse(script.textContent || "{}")
    } catch {
      return []
    }

    const byWorker = data[this.dateValue] || {}
    return byWorker[this.workerIdValue] || []
  }

  #shellIsStale() {
    return Boolean(this.dateValue) && this.dateValue !== this.#todayIso()
  }

  #todayIso() {
    const now = new Date()
    const pad = n => String(n).padStart(2, "0")
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  }

  #showBanner() {
    this.bannerTarget.classList.remove("d-none")
    this.bannerTarget.classList.add("d-flex")
  }

  #hideBanner() {
    this.bannerTarget.classList.remove("d-flex")
    this.bannerTarget.classList.add("d-none")
  }
}
